import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useId, useState } from "react";
import { cn } from "../utils";
import { textFieldContainerVariants, textFieldHelperVariants, textFieldIconVariants, textFieldInputVariants, textFieldLabelVariants, textFieldWrapperVariants, textFieldPrefixSuffixVariants, textFieldCounterVariants, } from "./text-field.styles";
/**
 * TextField component following Material Design 3 specifications.
 *
 * Supports filled and outlined variants with floating labels, helper text,
 * error states, icons, prefix/suffix text, and character counter.
 *
 * @example
 * ```tsx
 * <TextField label="Email" placeholder="Enter your email" />
 * <TextField variant="filled" label="Password" type="password" />
 * <TextField label="Username" error="Username is required" required />
 * <TextField label="Amount" type="number" prefixText="$" suffixText=".00" />
 * <TextField label="Bio" type="textarea" rows={4} maxLength={500} showCounter />
 * ```
 */
export function TextField({ variant = "outlined", label, helperText, error, startIcon, endIcon, fullWidth = false, disabled, className, id: providedId, onFocus, onBlur, onChange, onChangeText, value, defaultValue, placeholder, readOnly, type = "text", autoCapitalize, autoCorrect, autoComplete, inputMode, maxLength, minLength, min, max, step, pattern, name, required, testID, ref, rows = 2, cols = 20, prefixText, suffixText, showCounter = false, noAsterisk = false, 
// Exclude native-only props (intentionally unused for web compatibility)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
secureTextEntry: _secureTextEntry, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
multiline: _multiline, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
numberOfLines: _numberOfLines, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
onSubmit: _onSubmit, }) {
    const generatedId = useId();
    const id = providedId ?? generatedId;
    const helperId = `${id}-helper`;
    const hasError = Boolean(error);
    const [isFocused, setIsFocused] = useState(false);
    const [internalValue, setInternalValue] = useState(defaultValue ?? "");
    // Determine if label should float (only on focus or when has value, NOT on placeholder)
    const currentValue = value ?? internalValue;
    const hasValue = Boolean(currentValue);
    const shouldFloat = isFocused || hasValue;
    const isTextarea = type === "textarea";
    const handleFocus = () => {
        setIsFocused(true);
        onFocus?.();
    };
    const handleBlur = () => {
        setIsFocused(false);
        onBlur?.();
    };
    const handleChange = (e) => {
        const newValue = e.target.value;
        if (value === undefined) {
            setInternalValue(newValue);
        }
        onChange?.(e);
        onChangeText?.(newValue);
    };
    const currentLength = currentValue?.length ?? 0;
    const showCharCounter = showCounter && maxLength && maxLength > 0;
    let resolvedPlaceholder = placeholder;
    if (label) {
        resolvedPlaceholder = shouldFloat ? placeholder : undefined;
    }
    // Common input props
    const commonInputProps = {
        ref: ref,
        id,
        name,
        value,
        defaultValue,
        // For labeled fields, follow floating-label behavior. For unlabeled fields, show placeholder immediately.
        placeholder: resolvedPlaceholder,
        disabled,
        readOnly,
        required,
        autoCapitalize,
        autoCorrect: autoCorrect ? "on" : "off",
        autoComplete,
        inputMode,
        "aria-invalid": hasError,
        "aria-describedby": helperText || error ? helperId : undefined,
        "data-testid": testID,
        onFocus: handleFocus,
        onBlur: handleBlur,
        onChange: handleChange,
    };
    return (_jsxs("div", { className: cn(textFieldContainerVariants({ fullWidth }), className), children: [_jsxs("div", { className: textFieldWrapperVariants({
                    variant,
                    hasError,
                    disabled: Boolean(disabled),
                    isTextarea,
                }), children: [label && (_jsxs("label", { htmlFor: id, className: textFieldLabelVariants({
                            variant,
                            hasError,
                            focused: isFocused,
                            floating: shouldFloat,
                            disabled: Boolean(disabled),
                        }), children: [label, required && !noAsterisk && (_jsx("span", { className: "text-error ml-0.5", children: "*" }))] })), startIcon && (_jsx("span", { className: textFieldIconVariants({ position: "start", hasError }), children: startIcon })), prefixText && !isTextarea && shouldFloat && (_jsx("span", { className: textFieldPrefixSuffixVariants({ hasError }), children: prefixText })), isTextarea ? (_jsx("textarea", { ...commonInputProps, rows: rows, cols: cols, maxLength: maxLength, minLength: minLength, className: textFieldInputVariants({
                            variant,
                            hasStartIcon: Boolean(startIcon),
                            hasEndIcon: Boolean(endIcon),
                            isTextarea: true,
                        }) })) : (_jsx("input", { ...commonInputProps, type: type, maxLength: maxLength, minLength: minLength, min: min, max: max, step: step, pattern: pattern, className: textFieldInputVariants({
                            variant,
                            hasStartIcon: Boolean(startIcon),
                            hasEndIcon: Boolean(endIcon),
                            isTextarea: false,
                        }) })), suffixText && !isTextarea && shouldFloat && (_jsx("span", { className: textFieldPrefixSuffixVariants({ hasError }), children: suffixText })), endIcon && (_jsx("span", { className: textFieldIconVariants({ position: "end", hasError }), children: endIcon }))] }), (helperText || error || showCharCounter) && (_jsxs("div", { className: "flex items-start justify-between gap-2 px-4 pt-1", children: [_jsx("span", { id: helperId, className: textFieldHelperVariants({ hasError }), children: error ?? helperText }), showCharCounter && (_jsxs("span", { className: textFieldCounterVariants({ hasError }), children: [currentLength, " / ", maxLength] }))] }))] }));
}
//# sourceMappingURL=TextField.js.map